import axios from "axios";
import { Calendar, Clock, ShoppingBag } from "lucide-react";
import { useEffect, useState } from "react";
import { buildPeriodRange } from "../../../utils/helpers";
import ReportFilters from "./ReportFilters";

/* -------------------- KPI -------------------- */
const KPI = ({ title, value, subtext }) => (
  <div className="rounded-2xl border border-[#F7BFAD] bg-white px-5 py-4">
    <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
      {title}
    </p>
    <p className="text-2xl font-semibold text-gray-900">{value}</p>
  </div>
);

const SkeletonKPI = () => (
  <div className="bg-white border border-[#F7BFAD] rounded-2xl p-4 animate-pulse">
    <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
    <div className="h-8 w-32 bg-gray-200 rounded" />
  </div>
);

const SkeletonContent = () => (
  <div className="space-y-4 animate-pulse">
    <div className="h-6 w-48 bg-gray-200 rounded" />
    {[1, 2, 3].map((i) => (
      <div key={i} className="h-12 bg-gray-100 rounded" />
    ))}
  </div>
);
const StatusCard = ({ label, count }) => {
  return (
    <div className="relative rounded-xl border border-[#F7BFAD] bg-gradient-to-br from-[#FFF7F3] to-white p-5 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] overflow-hidden">
      {/* top accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#f0653a]" />

      {/* soft decorative bubble */}
      <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#f0653a]/10 rounded-full" />

      <div className="relative z-10 flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-gray-600 capitalize">{label}</p>
        <p className="text-4xl font-bold text-[#1f2937]">{count}</p>
      </div>
      {/* bottom accent */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#f0653a]/40" />
    </div>
  );
};

const DEFAULT_FILTERS = {
  fromDate: "",
  toDate: "",
  reportType: "monthly",
};

const BookingsReports = ({ branchId }) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const [data, setData] = useState({
    kpis: [],
    status: [],
    byDay: [],
    peakSlots: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!filters.reportType) return;

    const { fromDate, toDate } = buildPeriodRange(filters.reportType);
    setFilters((p) => ({ ...p, fromDate, toDate }));
  }, [filters.reportType]);
  const WEEKDAYS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const fetchBookings = async () => {
    try {
      setLoading(true);

      const params = {
        sections: "summary,statusBreakdown,byWeekday,peakSlots",
        fromDate: appliedFilters.fromDate,
        toDate: appliedFilters.toDate,
      };

      if (branchId) params.branchId = branchId;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/reports/bookings`,
        { params },
      );

      const api = res.data;

      // Normalize weekday data (ensure all 7 days exist)
      const weekdayMap = api.byWeekday.reduce((acc, item) => {
        acc[item.day % 7] = item.count;
        return acc;
      }, {});

      const normalizedByDay = WEEKDAYS.map((day, index) => ({
        day,
        count: weekdayMap[index] || 0,
      }));

      const statusColors = {
        completed: "#10b981",
        pending: "#f59e0b",
        cancelled: "#ef4444",
        verified: "#3b82f6",
      };

      setData({
        kpis: [
          {
            title: "Total Bookings",
            value: api.summary.totalBookings,
          },
          {
            title: "Completed",
            value: api.summary.completed,
            subtext: `${api.summary.completionRate}% success`,
          },
          {
            title: "Cancelled",
            value: api.summary.cancelled,
          },
          {
            title: "Completion Rate",
            value: `${api.summary.completionRate}%`,
          },
        ],

        status: api.statusBreakdown.map((s) => ({
          label: s.status,
          count: s.count,
          color: statusColors[s.status] || "#6b7280",
        })),

        byDay: normalizedByDay,

        peakSlots: api.peakSlots,
      });
    } catch (err) {
      console.error("Bookings report failed", err);
      setData({
        kpis: [],
        status: [],
        byDay: [],
        peakSlots: [],
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [appliedFilters, branchId]);

  const maxByDay = data.byDay.length
    ? Math.max(...data.byDay.map((d) => d.count))
    : 1;

  return (
    <div className="space-y-6">
      {/* FILTER BAR */}
      <ReportFilters
        filters={filters}
        appliedFilters={appliedFilters}
        setFilters={setFilters}
        onApply={() => setAppliedFilters(filters)}
        onClearAll={() => {
          setFilters(DEFAULT_FILTERS);
          setAppliedFilters(DEFAULT_FILTERS);
        }}
        onRemoveFilter={(key) => {
          setFilters((p) => ({ ...p, [key]: "" }));
          setAppliedFilters((p) => ({ ...p, [key]: "" }));
        }}
        onExportPDF={() =>
          alert(`Exporting ${appliedFilters.reportType} Bookings PDF`)
        }
        onExportExcel={() =>
          alert(`Exporting ${appliedFilters.reportType} Bookings Excel`)
        }
      />

      {/* KPI GRID */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => <SkeletonKPI key={i} />)
          : data?.kpis.map((k, i) => <KPI key={i} {...k} />)}
      </div>

      {/* CONTENT */}
      <div className="bg-white border border-[#F7BFAD] rounded-2xl p-6 space-y-6">
        {loading ? (
          <SkeletonContent />
        ) : !data.byDay.length ? (
          <div className="h-40 flex items-center justify-center text-sm text-gray-400">
            No booking data available
          </div>
        ) : (
          <div className="flex flex-col gap-y-8">
            {/* STATUS */}
            <div>
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <ShoppingBag className="size-5 text-[#f0653a]" />
                Booking Status
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                {data.status.map((s) => (
                  <StatusCard key={s.label} label={s.label} count={s.count} />
                ))}
              </div>
            </div>

            {/* BY DAY */}
            <div>
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Calendar className="size-5 text-[#f0653a]" />
                Bookings by Weekday
              </h3>

              <div className="space-y-2">
                {data.byDay.map((d) => (
                  <div key={d.day} className="flex items-center gap-3">
                    <span className="w-24 text-sm font-medium">{d.day}</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-[#f0653a] h-2 rounded-full"
                        style={{
                          width: `${(d.count / maxByDay) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="w-12 text-right font-semibold">
                      {d.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* PEAK SLOTS */}
            <div>
              <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                <Clock className="size-5 text-[#f0653a]" />
                Peak Booking Slots
              </h3>

              <div className="space-y-3">
                {data.peakSlots.map((slot, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center bg-[#FFF7F3] rounded-lg px-4 py-3"
                  >
                    <span className="font-medium">{slot.time}</span>
                    <span className="font-bold text-[#f0653a]">
                      {slot.bookings} bookings
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingsReports;
